import { Download, Maximize2 } from 'lucide-react'
import * as React from 'react'

import {
  BarChart,
  Button,
  LineChart,
  NetworkChart,
  SankeyChart,
} from '@/components/ui'
import { exportChartAsPNG } from '@/components/ui/chart/utils/exportChart'
import { buildArtifactFilename } from '@/modules/chat/utils/artifactFilename'
import ChartFilterBar from './ChartFilterBar'
import { chartPreviewCaption, isChartBusy } from './chart/density'
import {
  applyExpandedFilters,
  computeDateBounds,
  computeValueRange,
  isChartEmpty,
  isGraphSpec,
  resolveGraphLinks,
} from './chart/filters'
import { findSankeyCycle } from './chart/sankeyCycle'
import type {
  BarLineChartSpec,
  ChartSpec,
  ExpandedFilters,
  GraphChartSpec,
} from './chart/types'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'

export type { ChartSpec } from './chart/types'

interface ChartArtifactProps {
  spec: ChartSpec
}

const CHART_HEIGHT = 240
const SANKEY_HEIGHT = 360
const NETWORK_HEIGHT = 420

const DEFAULT_FILTERS: ExpandedFilters = {
  minValue: 0,
  hideExternal: false,
  topN: null,
  labelQuery: '',
  dateStart: null,
  dateEnd: null,
}

type ChartReadyApi = {
  containerRef: React.RefObject<HTMLDivElement | null>
  isFullscreen: boolean
  toggleFullscreen: () => Promise<void>
}

// Measures the live content width of a container via ResizeObserver. Drives
// the busy-chart decision, so dragging the chat panel wider re-renders the
// chart inline once it has room.
function useContainerWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = React.useState(0)
  React.useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(entry.contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])
  return width
}

export default function ChartArtifact({ spec }: ChartArtifactProps) {
  const [expanded, setExpanded] = React.useState(false)
  const [filters, setFilters] = React.useState<ExpandedFilters>(DEFAULT_FILTERS)
  const chartContainerRef =
    React.useRef<React.RefObject<HTMLDivElement | null> | null>(null)
  const inlineBodyRef = React.useRef<HTMLDivElement>(null)
  const inlineWidth = useContainerWidth(inlineBodyRef)

  const isEmpty = isChartEmpty(spec)
  const showPreview = !isEmpty && isChartBusy(spec, inlineWidth)
  const dataRange = React.useMemo(() => computeValueRange(spec), [spec])
  const dateBounds = React.useMemo(() => computeDateBounds(spec), [spec])
  const filteredSpec = React.useMemo(
    () => applyExpandedFilters(spec, filters),
    [spec, filters],
  )
  const totalCount = itemCount(spec)
  const shownCount = itemCount(filteredSpec)

  // Seed the slider at the bottom of the data range whenever the dialog
  // opens. Starting at $0 was meaningless because no flow is ever at $0,
  // so the user had no idea what scale they were filtering against.
  React.useEffect(() => {
    if (expanded) {
      setFilters((current) => ({ ...current, minValue: dataRange.min }))
    }
  }, [expanded, dataRange.min])

  const exportBaseFilename = React.useMemo(
    () => buildArtifactFilename(spec.title, 'chart'),
    [spec.title],
  )

  const handleChartReady = React.useCallback((api: ChartReadyApi) => {
    chartContainerRef.current = api.containerRef
  }, [])

  const handleDownload = React.useCallback(() => {
    const ref = chartContainerRef.current
    if (!ref) return
    exportChartAsPNG(ref, `${exportBaseFilename}.png`, {
      title: spec.title ?? undefined,
    })
  }, [exportBaseFilename, spec.title])

  const handleOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      setFilters(DEFAULT_FILTERS)
      chartContainerRef.current = null
    }
    setExpanded(open)
  }, [])

  const handleFilterChange = React.useCallback(
    (patch: Partial<ExpandedFilters>) => {
      setFilters((current) => ({ ...current, ...patch }))
    },
    [],
  )

  const handleResetFilters = React.useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS, minValue: dataRange.min })
  }, [dataRange.min])

  return (
    <>
      <figure className="my-1 w-full min-w-0 rounded-lg border bg-card p-4">
        <figcaption className="mb-3 flex items-center justify-between gap-3 text-sm font-medium text-foreground">
          <span className="min-w-0 flex-1 truncate" title={spec.title ?? ''}>
            {spec.title ?? ''}
          </span>
          {!isEmpty && (
            <Button
              type="button"
              theme="ghost"
              size="xs"
              isIcon
              className="shrink-0"
              onClick={() => setExpanded(true)}
              title="Expand"
              aria-label="Expand"
            >
              <Maximize2 className="size-3.5" aria-hidden />
            </Button>
          )}
        </figcaption>
        <div ref={inlineBodyRef} className="w-full min-w-0">
          {showPreview ? (
            <ChartPreview spec={spec} onExpand={() => setExpanded(true)} />
          ) : (
            <ChartBody spec={spec} />
          )}
        </div>
      </figure>

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={handleOpenChange}
        title={spec.title ?? 'Chart'}
        headerActions={
          isEmpty ? null : (
            <Button
              type="button"
              theme="ghost"
              size="sm"
              isIcon
              onClick={handleDownload}
              title="Download as PNG"
              aria-label="Download as PNG"
            >
              <Download className="size-4" aria-hidden />
            </Button>
          )
        }
      >
        <div className="flex min-h-0 flex-1 flex-col">
          {!isEmpty && (
            <ChartFilterBar
              spec={spec}
              filters={filters}
              dataRange={dataRange}
              dateBounds={dateBounds}
              shownCount={shownCount}
              totalCount={totalCount}
              onChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          )}
          <div className="flex min-h-0 flex-1 flex-col p-4">
            <ChartBody
              spec={filteredSpec}
              expanded
              onChartReady={handleChartReady}
            />
          </div>
        </div>
      </ExpandedArtifactDialog>
    </>
  )
}

// The number of filterable items in a chart: bars/points for bar-line,
// flows (links) for sankey/network. Drives the "showing X of Y" count.
function itemCount(spec: ChartSpec): number {
  if (isGraphSpec(spec)) return spec.links.length
  return spec.data?.length ?? 0
}

interface ChartBodyProps {
  spec: ChartSpec
  expanded?: boolean
  // Print/export rendering: category axes get wider tick spacing and
  // truncated labels so long merchant names don't collide on paper, where
  // there is no hover tooltip to fall back on.
  print?: boolean
  onChartReady?: (api: ChartReadyApi) => void
}

// Also rendered off-screen by the chat PDF exporter to capture chart images.
export function ChartBody({
  spec,
  expanded = false,
  print = false,
  onChartReady,
}: ChartBodyProps) {
  if (spec.type === 'sankey' || spec.type === 'network') {
    return (
      <GraphBody spec={spec} expanded={expanded} onChartReady={onChartReady} />
    )
  }
  return (
    <BarLineBody
      spec={spec}
      expanded={expanded}
      print={print}
      onChartReady={onChartReady}
    />
  )
}

interface ChartPreviewProps {
  spec: ChartSpec
  onExpand: () => void
}

// Stand-in for a chart that's too busy to read inline: the chart's shape with
// every SVG label hidden (labels are what overlap at narrow width), plus a
// one-line headline. The whole card opens the full, labelled chart.
function ChartPreview({ spec, onExpand }: ChartPreviewProps) {
  return (
    <button
      type="button"
      onClick={onExpand}
      title="View full chart"
      className="group relative block w-full cursor-pointer overflow-hidden rounded-lg border border-dashed bg-muted/10 text-left transition-colors hover:bg-muted/20"
    >
      <div
        className="pointer-events-none opacity-80 [&_text]:hidden"
        aria-hidden
      >
        <ChartBody spec={spec} />
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-card via-card/90 to-transparent px-3 pb-2 pt-8 text-xs">
        <span className="min-w-0 truncate text-muted-foreground">
          {chartPreviewCaption(spec)}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 font-medium text-foreground">
          <Maximize2 className="size-3" aria-hidden />
          View
        </span>
      </div>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 backdrop-blur-[1px] transition-opacity group-hover:opacity-100">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background shadow-sm">
          <Maximize2 className="size-4" aria-hidden />
          Click to expand
        </span>
      </div>
    </button>
  )
}

interface BarLineBodyProps {
  spec: BarLineChartSpec
  expanded: boolean
  print?: boolean
  onChartReady?: (api: ChartReadyApi) => void
}

function BarLineBody({
  spec,
  expanded,
  print = false,
  onChartReady,
}: BarLineBodyProps) {
  if (!spec.data || spec.data.length === 0) {
    return <ChartPlaceholder height={CHART_HEIGHT} expanded={expanded} />
  }
  if (spec.type === 'line') {
    // LineChart has no onChartReady hook-up (its useChartBase() takes no
    // callback), so the expanded-dialog PNG button can't capture it; the PDF
    // exporter reads the SVG straight from the DOM instead.
    return (
      <ChartFrame inlineHeight={CHART_HEIGHT} expanded={expanded}>
        <LineChart
          data={spec.data}
          height={CHART_HEIGHT}
          title={spec.title ?? undefined}
          valueFormat={spec.valueFormat ?? 'currency'}
        />
      </ChartFrame>
    )
  }
  return (
    <ChartFrame inlineHeight={CHART_HEIGHT} expanded={expanded}>
      <BarChart
        data={spec.data}
        height={CHART_HEIGHT}
        title={spec.title ?? undefined}
        valueFormat={spec.valueFormat ?? 'currency'}
        onChartReady={onChartReady}
        tickSpacing={print ? 110 : undefined}
        truncateXLabels={print}
        maxLabelLength={18}
      />
    </ChartFrame>
  )
}

interface GraphBodyProps {
  spec: GraphChartSpec
  expanded: boolean
  onChartReady?: (api: ChartReadyApi) => void
}

function GraphBody({ spec, expanded, onChartReady }: GraphBodyProps) {
  const height = spec.type === 'sankey' ? SANKEY_HEIGHT : NETWORK_HEIGHT

  if (spec.nodes.length === 0 || spec.links.length === 0) {
    return <ChartPlaceholder height={height} expanded={expanded} />
  }

  const resolvedLinks = resolveGraphLinks(spec.nodes, spec.links)
  if (resolvedLinks.length === 0) {
    return <ChartPlaceholder height={height} expanded={expanded} />
  }

  if (spec.type === 'sankey') {
    const cycle = findSankeyCycle(spec.nodes.length, resolvedLinks)
    if (cycle) {
      return (
        <ChartPlaceholder
          height={height}
          expanded={expanded}
          message={`Cannot render: sankey requires a DAG but the flow contains a cycle (${cycle
            .map((index) => spec.nodes[index]?.name ?? `#${index}`)
            .join(' → ')}). Ask the agent to net the flow or split nodes.`}
        />
      )
    }
    return (
      <ChartFrame inlineHeight={height} expanded={expanded}>
        <SankeyChart
          data={{ nodes: spec.nodes, links: resolvedLinks }}
          height={height}
          title={spec.title ?? undefined}
          valueFormat={spec.valueFormat ?? 'currency'}
          collapseToFit
          onChartReady={onChartReady}
        />
      </ChartFrame>
    )
  }

  return (
    <ChartFrame inlineHeight={height} expanded={expanded}>
      <NetworkChart
        data={{ nodes: spec.nodes, links: resolvedLinks }}
        height={height}
        title={spec.title ?? undefined}
        valueFormat={spec.valueFormat ?? 'currency'}
        onChartReady={onChartReady}
      />
    </ChartFrame>
  )
}

interface ChartFrameProps {
  inlineHeight: number
  expanded: boolean
  children: React.ReactNode
}

function ChartFrame({ inlineHeight, expanded, children }: ChartFrameProps) {
  if (expanded) {
    return <div className="min-h-0 flex-1">{children}</div>
  }
  return <div style={{ height: inlineHeight }}>{children}</div>
}

interface ChartPlaceholderProps {
  height: number
  expanded: boolean
  message?: string
}

function ChartPlaceholder({
  height,
  expanded,
  message = 'No data to chart.',
}: ChartPlaceholderProps) {
  const baseClasses =
    'flex items-center justify-center text-center text-sm text-muted-foreground'
  const padding = message === 'No data to chart.' ? '' : 'px-6'

  if (expanded) {
    return (
      <div className={`${baseClasses} ${padding} min-h-0 flex-1`}>
        {message}
      </div>
    )
  }
  return (
    <div style={{ height }} className={`${baseClasses} ${padding}`}>
      {message}
    </div>
  )
}
